import { isValidCpf } from '@voll-intelligence/domain';
import type { GenUiAction } from '../../../shared/ui/action.js';
import {
  type TravelerRef,
  allocationOwnerId,
  hotelRooms,
  type CorporateBookingForm,
  type Allocation,
  type CheckoutItem,
  type CheckoutSubmit,
  type CheckoutTravelerBlock,
  type CheckoutTravelerDraft,
  type CheckoutTravelerEdits,
  type CheckoutTravelDocs,
  type NamedRef,
} from '@voll-intelligence/genui-contract';
import { travelDocsAck } from './travel_docs.js';
import { resolveCostCenter, type AllocationDraft } from '@voll-intelligence/intelligence-ui-kit';
import {
  productContract,
  type CheckoutAllocationOwner,
} from '@voll-intelligence/intelligence-ui-kit/checkout';

/** Resolves an id to its `{id,name}` from an already-fetched option list —
 *  the CLIENT-SIDE echo that lets the reserve mapper skip a server refetch
 *  (design spec §"Reserve data resolution"). Fail-closed: an id the form
 *  doesn't recognize throws rather than submitting a corrupted label. */
function namedRef(
  label: string,
  id: string,
  options: readonly { id: string; name: string }[],
): NamedRef {
  const option = options.find((o) => o.id === id);
  if (!option) throw new Error(`${label} "${id}" não encontrado no formulário — reabra o checkout`);
  return { id: option.id, name: option.name };
}

/** Resolves one picked allocation row into the wire `Allocation`, with its
 *  `percent` when it is part of a split (#6697; a lone row goes without, as
 *  before). Fail-closed on an unknown company/costCenter/project id, mirroring
 *  `namedRef`. */
function resolveAllocation(form: CorporateBookingForm, allocation: AllocationDraft): Allocation {
  const company = form.companies.find((c) => c.id === allocation.companyId);
  if (!company) throw new Error(`empresa "${allocation.companyId}" não encontrada no formulário`);
  // A escolha vinda da BUSCA remota não está no catálogo do form por definição —
  // resolvê-la só pelo catálogo derrubaria toda seleção buscada no fail-closed.
  // `resolveCostCenter` consulta o catálogo primeiro e cai no eco do draft.
  const found = resolveCostCenter(form, allocation);
  const costCenter: NamedRef = found
    ? { id: found.id, name: found.name }
    : namedRef('centro de custo', allocation.costCenterId, company.costCenters);
  const project = allocation.projectId
    ? namedRef('projeto', allocation.projectId, company.projects)
    : undefined;
  return {
    company: { id: company.id, name: company.name },
    costCenter,
    ...(project ? { project } : {}),
    ...(allocation.percent === undefined ? {} : { percent: allocation.percent }),
  };
}

/** Resolves the draft document into the wire payload. A CPF on file is dropped
 *  (the backend resolves it by traveler id); without one, the typed CPF travels in
 *  digits (#5809). A blank passport/DNI number means "reuse the on-file
 *  document" — we resend the backend's masked value to signal that, matching the
 *  legacy rental submit contract. */
function resolveDocument(
  document: NonNullable<CheckoutTravelerDraft['document']>,
  traveler: CorporateBookingForm['traveler'],
): CheckoutTravelerEdits['document'] | undefined {
  if (document.type === 'cpf') return typedCpf(document.value, traveler);
  if (document.value.trim()) {
    return {
      type: document.type,
      value: document.value,
      ...(document.issuingCountry ? { issuingCountry: document.issuingCountry } : {}),
      ...(document.expirationDate ? { expirationDate: document.expirationDate } : {}),
    };
  }
  return maskedDocument(document.type, traveler);
}

function typedCpf(
  value: string,
  traveler: CorporateBookingForm['traveler'],
): CheckoutTravelerEdits['document'] | undefined {
  if (traveler?.document?.type === 'cpf') return undefined;
  // Só um CPF válido viaja: com outro documento em ficha nada o valida antes
  // (`document` não falta), e um número incompleto iria à reserva e ao perfil.
  const digits = value.replaceAll(/\D/g, '');
  return isValidCpf(digits) ? { type: 'cpf', value: digits } : undefined;
}

function maskedDocument(
  type: Exclude<NonNullable<CheckoutTravelerDraft['document']>['type'], 'cpf'>,
  traveler: CorporateBookingForm['traveler'],
): CheckoutTravelerEdits['document'] | undefined {
  const onFile = traveler?.document;
  const passportOnFile = onFile?.type === 'passport' ? onFile.value : undefined;
  const dniOnFile = onFile?.type === 'dni' ? onFile.value : undefined;
  const masked =
    type === 'passport'
      ? (traveler?.maskedInfo?.passport ?? passportOnFile)
      : (traveler?.maskedInfo?.dni ?? dniOnFile);
  return masked ? { type, value: masked } : undefined;
}

export function toTravelerEdits(
  draft: CheckoutTravelerDraft,
  traveler: CorporateBookingForm['traveler'],
): CheckoutTravelerEdits {
  const { document, boardingName, boardingSurname, phone, ...rest } = draft;
  const edits: CheckoutTravelerEdits = {
    ...rest,
    ...(boardingName?.trim() ? { boardingName } : {}),
    ...(boardingSurname?.trim() ? { boardingSurname } : {}),
    ...(phone?.trim() ? { phone } : {}),
  };
  if (!document) return edits;
  const resolved = resolveDocument(document, traveler);
  return resolved ? { ...edits, document: resolved } : edits;
}

/** One resolved traveler for the multi-traveler builder — the traveler ref plus
 *  that traveler's own fetched form, picked allocation, and identity draft. */
interface MultiTravelerEntry {
  traveler: TravelerRef;
  form: CorporateBookingForm;
  allocations: readonly AllocationDraft[];
  travelerDraft: CheckoutTravelerDraft;
}

export interface BuildMultiTravelerCheckoutSubmitArgs {
  challengeId: string;
  item: CheckoutItem;
  perTraveler: MultiTravelerEntry[];
  motiveId: string;
  justificationId: string | undefined;
  observation: string;
  policyTravelerId?: string;
  /** `'requester'` (hotel e carro, `productContract`): só o bloco do dono do rateio
   *  (`allocationOwnerId` — o requester, ou o hóspede da política quando ele não
   *  é hóspede) carrega `allocations`. Default `'every-traveler'` (voo: todo
   *  bloco aloca). */
  allocationOwner?: CheckoutAllocationOwner;
  requesterId?: string;
  /** Os documentos da viagem que a pessoa marcou como «Tenho». */
  travelDocsChecked?: readonly string[];
  /** Os documentos que a seção mostrou: vão como a ciência do viajante. */
  travelDocs?: CheckoutTravelDocs;
}

/** Product-agnostic submit mapper — form state → the `product-checkout-submit`
 *  action, and the one place that turns raw ids into echoed `NamedRef`s. One
 *  `CheckoutTravelerBlock` per traveler — each carries that traveler's identity edits, but only the
 *  allocation OWNER's block carries `allocations` (voo: todos; hotel: só o
 *  requester) — plus the booking-level motive/justification/observation and
 *  the `policyTravelerId` that governs policy. Motive/justification lists are
 *  token-scoped and identical across travelers, so they resolve against the
 *  FIRST traveler's form. */
export function buildMultiTravelerCheckoutSubmit(
  args: BuildMultiTravelerCheckoutSubmitArgs,
): Extract<GenUiAction, { type: 'product-checkout-submit' }> {
  const form = buildMultiTravelerBookingForm({
    ...args,
    guestsPerRoom: productContract(args.item).guestsPerRoom(args.item),
  });
  return {
    type: 'product-checkout-submit',
    challengeId: args.challengeId,
    productType: args.item.productType,
    form: { ...legacySubmitEcho(args.item, form.travelers), ...form },
  };
}

/** Transição da #4509, a remover quando o agents com o leitor novo estiver em
 *  produção: o agents de mobilidade anterior lê só `allocations` e
 *  `traveler.phone` do topo do envio, e o deploy de produção sobe o web-chat
 *  ANTES do agents. Com um viajante só, o envio de mobilidade repete o bloco no
 *  formato antigo (o contrato do produto diz quem: `legacySubmitEcho`). Quem normaliza (`normalizeCheckoutTravelers`, o BFF) usa
 *  `travelers[]`, que tem precedência. */
function legacySubmitEcho(
  item: CheckoutItem,
  travelers: CheckoutTravelerBlock[],
): Pick<CheckoutSubmit, 'allocations' | 'traveler'> {
  const [only, ...others] = travelers;
  if (!productContract(item).legacySubmitEcho || !only || others.length > 0 || !only.allocations) {
    return {};
  }
  return { allocations: only.allocations, ...(only.edits ? { traveler: only.edits } : {}) };
}

/** A metade CORPORATIVA do submit multi-viajante, sem o envelope da ação —
 *  compartilhada com o `concierge_checkout`, cujo payload leva os mesmos
 *  blocos de viajante e motivo/justificativa/observação ao lado do pedido. */
export function buildMultiTravelerBookingForm(
  args: Omit<BuildMultiTravelerCheckoutSubmitArgs, 'challengeId' | 'item'> & {
    /** Hóspedes por quarto (hotel): cada titular leva o seu rateio (#5522). */
    guestsPerRoom?: number;
  },
): CheckoutSubmit & { travelers: CheckoutTravelerBlock[] } {
  const first = args.perTraveler[0];
  if (!first) throw new Error('checkout multi-traveler sem viajantes — reabra o checkout');

  const allocationOwner = args.allocationOwner ?? 'every-traveler';
  const ownerId = allocationOwnerId(
    args.perTraveler.map((entry) => entry.traveler.id),
    args.requesterId,
    args.policyTravelerId,
  );
  // No modo `'requester'`, o titular de cada quarto leva o rateio (#5522); sem
  // a ocupação, só o dono.
  const holderIds = new Set(
    hotelRooms(
      args.perTraveler.map((entry) => entry.traveler.id),
      ownerId,
      args.guestsPerRoom,
    ).map((room) => room[0]),
  );
  const ownsAllocation = (entry: MultiTravelerEntry): boolean =>
    allocationOwner === 'every-traveler' || holderIds.has(entry.traveler.id);
  if (allocationOwner === 'requester' && ownerId === undefined) {
    throw new Error('checkout sem o bloco dono do rateio — reabra o checkout');
  }

  const travelers: CheckoutTravelerBlock[] = args.perTraveler.map((entry) => ({
    travelerId: entry.traveler.id,
    ...(ownsAllocation(entry)
      ? { allocations: entry.allocations.map((row) => resolveAllocation(entry.form, row)) }
      : {}),
    edits: toTravelerEdits(entry.travelerDraft, entry.form.traveler),
  }));

  const motive = namedRef('motivo', args.motiveId, first.form.motives);
  const justification = args.justificationId
    ? namedRef('justificativa', args.justificationId, first.form.justifications)
    : undefined;

  return {
    travelers,
    ...(args.policyTravelerId ? { policyTravelerId: args.policyTravelerId } : {}),
    motive,
    ...(justification ? { justification } : {}),
    ...(args.observation ? { observation: args.observation } : {}),
    ...(args.travelDocsChecked?.length ? { travelDocsChecked: [...args.travelDocsChecked] } : {}),
    ...(args.travelDocs ? { travelDocsAck: travelDocsAck(args.travelDocs) } : {}),
  };
}
